import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, People, PlayArrow as PlayArrowIcon, Stop as StopIcon } from '@mui/icons-material';
import axios from '../../utils/axios';
import AddBotDialog from './AddBotDialog';
import EditBotDialog from './EditBotDialog';
import PrivateBotAccessDialog from './PrivateBotAccessDialog';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

function formatStartError(error, bot) {
  const response = error?.response;
  const rawData = response?.data;
  const status = Number.isInteger(response?.status) && response.status >= 100 && response.status <= 599 ? response.status : null;
  const requestCode = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED', 'ERR_BAD_RESPONSE', 'ERR_BAD_REQUEST'].includes(error?.code) ? error.code : '';
  let data = rawData;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = null;
    }
  }
  const isJson = data !== null && typeof data === 'object' && !Array.isArray(data);
  const isHtml = typeof rawData === 'string' && (/^\s*</.test(rawData) || String(response?.headers?.['content-type'] || '').includes('text/html'));
  const responseFormat = !response ? 'ответ не получен' : isHtml ? 'HTML' : isJson ? 'JSON' :
    rawData == null || rawData === '' ? 'пустой' : typeof rawData === 'string' ? 'текст, не JSON' : 'неизвестный';
  const sanitize = value => {
    if (typeof value !== 'string') return '';
    let text = value.replace(/(?:https?|mongodb(?:\+srv)?):\/\/[^\s<>"']+/gi, '[URL]');
    for (const secret of [bot?.token, bot?.token?.split(':')[1]].filter(value => typeof value === 'string' && value)) {
      text = text.split(secret).join('[REDACTED]').split(encodeURIComponent(secret)).join('[REDACTED]');
    }
    return text.replace(/\b\d{5,}:[A-Za-z0-9_-]+\b/g, '[TOKEN]')
      .replace(/\bBearer\s+[^\s,;"']+/gi, 'Bearer [REDACTED]')
      .replace(/\b(?:[\w-]*(?:token|secret|password)|api[_-]?key|authorization)["']?\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;}]+)/gi, '[REDACTED]')
      .replace(/\p{Cc}/gu, ' ').trim().slice(0, 1500);
  };
  const reason = isJson ? sanitize(data.diagnostic?.reason) : '';
  let message = isJson ? sanitize(data.message) || sanitize(data.error) || sanitize(data.error?.message) || reason : '';
  if (reason && !message.includes(reason)) message += `\nПричина: ${reason}`;
  if (!message) {
    const httpReasons = {
      401: 'Сервер отклонил авторизацию. Войдите в панель заново.',
      403: 'Сервер запретил запрос запуска. Проверьте права доступа вашей учётной записи.',
      404: 'Not Found: сервер не нашёл запрошенный ресурс. Проверьте маршрут API и версию backend.',
      502: 'Bad Gateway: сервер или прокси вернул ошибку шлюза без подробной причины запуска.',
      503: 'Service Unavailable: сервер временно недоступен и не передал причину запуска.',
      504: 'Gateway Timeout: сервер или прокси сообщил об истечении времени ожидания. Итог запуска неизвестен.'
    };
    if (status) {
      message = `HTTP ${status}. ${httpReasons[status] || 'Сервер не передал текст причины ошибки запуска.'}`;
    } else if (['ECONNABORTED', 'ETIMEDOUT'].includes(requestCode)) {
      message = 'Истекло время ожидания HTTP-ответа от backend. Итог запуска неизвестен; запрос мог продолжить выполняться на сервере.';
    } else if (requestCode === 'ERR_NETWORK') {
      message = 'Браузер не получил HTTP-ответ от backend. Возможны недоступность API, сбой сети, TLS или блокировка CORS; Axios не позволяет различить их. Подробности смотрите в Network/Console браузера.';
    } else if (requestCode === 'ERR_CANCELED') {
      message = 'Запрос запуска отменён в браузере. Итог выполнения на сервере неизвестен.';
    } else {
      message = 'Причина запуска отсутствует в доступном ответе. Проверьте запрос во вкладке Network браузера и логи backend; по этим данным нельзя установить причину сбоя.';
    }
    if (response) message += ` Формат ответа: ${responseFormat}. Диагностика backend не получена; проверьте логи backend и прокси.`;
  }
  return {
    message, httpStatus: status || (response ? 'не указан' : 'ответ не получен'), responseFormat,
    requestCode, stage: isJson ? sanitize(data.diagnostic?.stage) : '', code: isJson ? sanitize(data.diagnostic?.code) : ''
  };
}

const isPending = operation => ['queued', 'starting'].includes(operation?.status);
const isRunning = bot => bot.isRunning ?? bot.isActive;
const unknownStartup = 'Backend перезапущен или операция больше недоступна. Итог запуска неизвестен. Обновите статус; автоматического повторного запуска нет.';

export default function TelegramBots() {
  const [bots, setBots] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [accessBot, setAccessBot] = useState(null);
  const [startError, setStartError] = useState(null);
  const mountedRef = useRef(false);
  const jobsRef = useRef(new Map());
  const pollingRef = useRef({ timer: null, inFlight: false, controller: null });
  const revisionsRef = useRef(new Map());
  const listRequestRef = useRef(0);
  const { enqueueSnackbar } = useSnackbar();

  const updateBots = useCallback(patches => {
    if (!mountedRef.current || !patches.size) return;
    patches.forEach((changes, botId) => {
      revisionsRef.current.set(botId, (revisionsRef.current.get(botId) || 0) + 1);
    });
    setBots(current => current.map(bot => patches.has(bot._id) ? { ...bot, ...patches.get(bot._id) } : bot));
  }, []);

  const updateBot = useCallback((botId, changes) => {
    updateBots(new Map([[botId, changes]]));
  }, [updateBots]);

  const showStartError = useCallback((bot, error, request) => {
    setStartError({
      botName: bot.username || bot._id, request, time: new Date().toISOString(),
      ...formatStartError(error, bot),
    });
  }, []);

  const applyOperation = useCallback((bot, job, data, status, request, patches) => {
    if (!mountedRef.current || jobsRef.current.get(bot._id) !== job) return false;
    let operation = data.operation;
    if (!operation || (job.operationId && operation.operationId !== job.operationId) ||
        (operation.botId && operation.botId !== bot._id) ||
        !['queued', 'starting', 'succeeded', 'failed', 'cancelled', 'unknown'].includes(operation.status)) {
      operation = { operationId: job.operationId, botId: bot._id, status: 'unknown' };
    }
    job.operationId = operation.operationId;
    if (isPending(operation) && !operation.operationId) operation = { ...operation, status: 'unknown' };
    const runtime = data.runtime || {};
    const changes = {
      ...runtime,
      ...(operation.status === 'succeeded' && runtime.isRunning == null && runtime.isActive == null
        ? { isRunning: true, isActive: true } : {}),
      startup: operation, startBusy: false, pollError: '', pollingBlocked: false, launchUncertain: false,
    };
    if (patches) patches.set(bot._id, changes);
    else updateBot(bot._id, changes);
    if (isPending(operation)) return true;
    jobsRef.current.delete(bot._id);
    if (job.manual && operation.status === 'succeeded') enqueueSnackbar('Бот успешно запущен', { variant: 'success' });
    if (job.manual && operation.status === 'failed') {
      showStartError(bot, { response: { status, data: operation } }, request);
    }
    return false;
  }, [enqueueSnackbar, showStartError, updateBot]);

  const pollStartups = useCallback(async function pollPass() {
    const polling = pollingRef.current;
    if (!mountedRef.current || polling.inFlight) return;
    clearTimeout(polling.timer);
    polling.timer = null;
    polling.inFlight = true;
    const trackedJobs = () => [...jobsRef.current.values()].filter(job => job.bot && job.operationId && !job.stopping);
    const snapshot = trackedJobs();
    let batchMode = snapshot.length > 5;
    const isCurrent = job => mountedRef.current && pollingRef.current === polling && jobsRef.current.get(job.bot._id) === job;
    try {
      for (let offset = 0; offset < snapshot.length;) {
        if (!mountedRef.current || pollingRef.current !== polling) break;
        batchMode = batchMode || trackedJobs().length > 5;
        const useBatch = batchMode;
        const size = useBatch ? 100 : 1;
        const batch = snapshot.slice(offset, offset + size).filter(isCurrent);
        offset += size;
        if (!batch.length) continue;
        const path = useBatch ? '/telegram-bots/start-status' : `/telegram-bots/${batch[0].bot._id}/start-status`;
        polling.controller = useBatch ? new AbortController() : batch[0].controller;
        try {
          const { data, status } = useBatch
            ? await axios.post(path, {
              operations: batch.map(job => ({ botId: job.bot._id, operationId: job.operationId })),
            }, { signal: polling.controller.signal, timeout: 15000 })
            : await axios.get(path, {
              params: { operationId: batch[0].operationId }, signal: polling.controller.signal, timeout: 15000,
            });
          const results = new Map();
          if (useBatch && Array.isArray(data?.operations)) {
            data.operations.forEach(result => {
              const botId = result?.operation?.botId;
              if (botId) results.set(botId, results.has(botId) ? null : result);
            });
          }
          const patches = new Map();
          batch.forEach(job => {
            if (!isCurrent(job)) return;
            applyOperation(job.bot, job, (useBatch ? results.get(job.bot._id) : data) || {}, status, `${useBatch ? 'POST' : 'GET'} ${path}`, patches);
          });
          updateBots(patches);
        } catch (error) {
          const patches = new Map();
          const status = error?.response?.status;
          const permanent = [400, 401, 403, 404].includes(status);
          batch.forEach(job => {
            if (!isCurrent(job)) return;
            if (permanent) {
              jobsRef.current.delete(job.bot._id);
              patches.set(job.bot._id, {
                startup: { operationId: job.operationId, botId: job.bot._id, status: 'unknown' },
                pollingBlocked: true,
                pollError: `HTTP ${status}: проверка статуса приостановлена. ${formatStartError(error, job.bot).message} Итог запуска неизвестен. Проверьте авторизацию, права доступа и API, затем нажмите «Обновить». Автоматического повторного запуска нет.`,
              });
            } else {
              patches.set(job.bot._id, { pollError: 'Не удалось получить статус. Запуск не признан неудачным. Повторная проверка через 2 секунды.' });
            }
          });
          updateBots(patches);
        } finally {
          polling.controller = null;
        }
      }
    } finally {
      polling.inFlight = false;
      if (mountedRef.current && pollingRef.current === polling && trackedJobs().length) polling.timer = setTimeout(pollPass, 2000);
    }
  }, [applyOperation, updateBots]);

  const trackStartup = useCallback((bot, job) => {
    job.bot = bot;
    job.poll = pollStartups;
    const polling = pollingRef.current;
    if (!polling.inFlight && polling.timer === null) polling.timer = setTimeout(pollStartups, 2000);
  }, [pollStartups]);

  useEffect(() => {
    mountedRef.current = true;
    const jobs = jobsRef.current;
    const polling = { timer: null, inFlight: false, controller: null };
    pollingRef.current = polling;
    return () => {
      mountedRef.current = false;
      clearTimeout(polling.timer);
      polling.controller?.abort();
      jobs.forEach(job => job.controller.abort());
      jobs.clear();
    };
  }, []);

  useEffect(() => {
    const botIds = new Set(bots.map(bot => bot._id));
    jobsRef.current.forEach((job, botId) => {
      if (botIds.has(botId)) return;
      job.controller.abort();
      jobsRef.current.delete(botId);
    });
    bots.forEach(bot => {
      if (!isPending(bot.startup) || jobsRef.current.has(bot._id)) return;
      if (!bot.startup.operationId) {
        updateBot(bot._id, { startup: { ...bot.startup, status: 'unknown' } });
        return;
      }
      const job = { operationId: bot.startup.operationId, controller: new AbortController() };
      jobsRef.current.set(bot._id, job);
      trackStartup(bot, job);
    });
    if (![...jobsRef.current.values()].some(job => job.bot && job.operationId && !job.stopping)) {
      clearTimeout(pollingRef.current.timer);
      pollingRef.current.timer = null;
    }
  }, [bots, trackStartup, updateBot]);
  const [botTypeFilter, setBotTypeFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeletingBlocked, setIsDeletingBlocked] = useState(false);
  const deletingBlockedRef = useRef(false);
  const blockedBotCount = bots.filter(bot => bot.isBlocked === true).length;
  const [operatorUsername, setOperatorUsername] = useState('');
  const [operatorStatus, setOperatorStatus] = useState('loading');
  const [operatorValidation, setOperatorValidation] = useState('');
  const [operatorLoadAttempt, setOperatorLoadAttempt] = useState(0);
  const operatorSessionRef = useRef(0);
  const operatorSaveRef = useRef(null);
  const normalizedOperator = operatorUsername.trim().replace(/^@/, '').toLowerCase();
  const validOperator = operatorUsername.trim() === '' || /^[a-z0-9_]{1,32}$/.test(normalizedOperator);

  useEffect(() => {
    const session = ++operatorSessionRef.current;
    if (botTypeFilter !== 'private') return;
    setOperatorStatus('loading');
    setOperatorUsername('');
    setOperatorValidation('');
    const loadOperator = async () => {
      if (operatorSaveRef.current) {
        await operatorSaveRef.current.catch(() => undefined);
      }
      if (operatorSessionRef.current !== session) return;
      try {
        const { data } = await axios.get('/telegram-bots/private-settings');
        if (operatorSessionRef.current !== session) return;
        setOperatorUsername(data.private_operator_username);
        setOperatorStatus('ready');
      } catch (error) {
        if (operatorSessionRef.current === session) setOperatorStatus('error');
      }
    };
    loadOperator();
    return () => { operatorSessionRef.current = session + 1; };
  }, [botTypeFilter, operatorLoadAttempt]);

  const handleSaveOperator = async () => {
    if (botTypeFilter !== 'private' || operatorStatus !== 'ready' || operatorSaveRef.current) return;
    if (!validOperator) {
      setOperatorValidation('Введите от 1 до 32 латинских букв, цифр или подчёркиваний, с необязательным @. Для сброса очистите поле.');
      return;
    }
    const session = operatorSessionRef.current;
    setOperatorStatus('saving');
    const request = axios.patch('/telegram-bots/private-settings', { private_operator_username: normalizedOperator });
    operatorSaveRef.current = request;
    try {
      const { data } = await request;
      if (operatorSessionRef.current !== session) return;
      setOperatorUsername(data.private_operator_username);
      enqueueSnackbar('Оператор для всех приватных ботов сохранён', { variant: 'success' });
    } catch (error) {
      if (operatorSessionRef.current === session) {
        enqueueSnackbar('Ошибка при сохранении оператора. Повторите попытку.', { variant: 'error' });
      }
    } finally {
      operatorSaveRef.current = null;
      if (operatorSessionRef.current === session) setOperatorStatus('ready');
    }
  };

  // Состояния для рассылки
  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastBotId, setBroadcastBotId] = useState(null);

  // Загрузка списка ботов
  const fetchBots = async () => {
    const request = ++listRequestRef.current;
    const revisions = new Map(revisionsRef.current);
    setIsRefreshing(true);
    try {
      const response = await axios.get('/telegram-bots');
      if (!mountedRef.current || listRequestRef.current !== request) return;
      setBots(current => {
        const previous = new Map(current.map(bot => [bot._id, bot]));
        return response.data.map(bot => previous.has(bot._id) &&
          (jobsRef.current.has(bot._id) || revisions.get(bot._id) !== revisionsRef.current.get(bot._id))
          ? previous.get(bot._id) : bot);
      });
    } catch (error) {
      if (mountedRef.current && listRequestRef.current === request) enqueueSnackbar('Ошибка при загрузке списка ботов', { variant: 'error' });
    } finally {
      if (mountedRef.current && listRequestRef.current === request) setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  // Переключение статуса бота
  const handleToggleStatus = async (id) => {
    try {
      await axios.patch(`/telegram-bots/${id}/toggle-status`);
      fetchBots();
      enqueueSnackbar('Статус бота успешно изменен', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Ошибка при изменении статуса бота', { variant: 'error' });
    }
  };

  // Удаление бота
  const handleDelete = async (id) => {
    if (deletingBlockedRef.current) return;
    if (window.confirm('Вы уверены, что хотите удалить этого бота?')) {
      try {
        await axios.delete(`/telegram-bots/${id}`);
        fetchBots();
        enqueueSnackbar('Бот успешно удален', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Ошибка при удалении бота', { variant: 'error' });
      }
    }
  };

  const handleDeleteBlocked = async () => {
    if (deletingBlockedRef.current || isRefreshing) return;
    const botIds = bots.filter(bot => bot.isBlocked === true).map(bot => bot._id);
    if (botIds.length === 0) return;
    if (!window.confirm(`Удалить заблокированных ботов из панели: ${botIds.length}? Удаление необратимо. Это действие не удаляет ботов из Telegram.`)) return;

    deletingBlockedRef.current = true;
    setIsDeletingBlocked(true);
    try {
      const { data: { deletedCount, skippedCount, failedCount } } = await axios.delete('/telegram-bots/blocked', {
        data: { botIds },
      });
      if (skippedCount > 0 || failedCount > 0) {
        enqueueSnackbar(`Удалено заблокированных ботов: ${deletedCount}. Пропущено: ${skippedCount}. Ошибок: ${failedCount}`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Удалено заблокированных ботов: ${deletedCount}`, { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Ошибка при удалении заблокированных ботов', { variant: 'error' });
    } finally {
      await fetchBots();
      deletingBlockedRef.current = false;
      setIsDeletingBlocked(false);
    }
  };

  // Открытие диалога редактирования
  const handleEdit = (bot) => {
    if (deletingBlockedRef.current) return;
    setSelectedBot(bot);
    setIsEditDialogOpen(true);
  };

  // Открытие попапа рассылки
  const handleOpenBroadcast = (botId) => {
    if (deletingBlockedRef.current || bots.find(bot => bot._id === botId)?.isBlocked) return;
    setBroadcastBotId(botId);
    setBroadcastText('');
    setIsBroadcastOpen(true);
  };

  // Закрытие попапа рассылки
  const handleCloseBroadcast = () => {
    setIsBroadcastOpen(false);
    setBroadcastText('');
    setBroadcastBotId(null);
  };

  // Отправка рассылки
  const handleSendBroadcast = async () => {
    if (bots.find(bot => bot._id === broadcastBotId)?.isBlocked) return;
    if (!broadcastText.trim()) {
      enqueueSnackbar('Введите текст для рассылки', { variant: 'warning' });
      return;
    }
    try {
     axios.post(`/telegram-bots/broadcast/${broadcastBotId}`, { message: broadcastText });
      enqueueSnackbar('Рассылка успешно отправлена', { variant: 'success' });
      handleCloseBroadcast();
    } catch (error) {
      enqueueSnackbar('Ошибка при отправке рассылки', { variant: 'error' });
    }
  };

  // Запуск бота
  const handleStartBot = async (botId) => {
    const bot = bots.find(bot => bot._id === botId);
    if (!bot || deletingBlockedRef.current || bot.isBlocked || isRunning(bot) ||
        jobsRef.current.has(botId) || isPending(bot.startup) || bot.launchUncertain || bot.pollingBlocked) return;
    const job = { controller: new AbortController(), manual: true };
    jobsRef.current.set(botId, job);
    updateBot(botId, { startBusy: true, startup: null, pollError: '' });
    const path = `/telegram-bots/${botId}/start`;
    try {
      const { data, status } = await axios.post(path);
      if (!mountedRef.current || jobsRef.current.get(botId) !== job) return;
      if (data.operation || status === 202) {
        if (applyOperation(bot, job, data, status, `POST ${path}`)) trackStartup(bot, job);
      } else {
        jobsRef.current.delete(botId);
        updateBot(botId, { ...data.bot, isRunning: data.bot?.isRunning ?? data.bot?.isActive ?? true, startBusy: false });
        enqueueSnackbar('Бот успешно запущен', { variant: 'success' });
        if (!data.bot) fetchBots();
      }
    } catch (error) {
      if (!mountedRef.current || jobsRef.current.get(botId) !== job) return;
      jobsRef.current.delete(botId);
      updateBot(botId, { startBusy: false, launchUncertain: !error?.response || error.response.status >= 500 });
      showStartError(bot, error, `POST ${path}`);
      fetchBots();
    }
  };

  // Остановка бота
  const handleStopBot = async (botId) => {
    if (deletingBlockedRef.current || jobsRef.current.get(botId)?.stopping) return;
    const bot = bots.find(bot => bot._id === botId);
    const previous = jobsRef.current.get(botId);
    previous?.controller.abort();
    const job = { stopping: true, controller: new AbortController() };
    jobsRef.current.set(botId, job);
    updateBot(botId, { stopBusy: true });
    try {
      const { data } = await axios.post(`/telegram-bots/${botId}/stop`);
      if (!mountedRef.current || jobsRef.current.get(botId) !== job) return;
      jobsRef.current.delete(botId);
      updateBot(botId, {
        ...data.bot, ...data.runtime, isRunning: false, isActive: false,
        startup: null, startBusy: false, stopBusy: false, pollError: '', pollingBlocked: false, launchUncertain: false,
      });
      enqueueSnackbar('Бот успешно остановлен', { variant: 'success' });
    } catch (error) {
      if (!mountedRef.current || jobsRef.current.get(botId) !== job) return;
      jobsRef.current.delete(botId);
      if (previous?.operationId && isPending(bot.startup)) {
        const resumed = { operationId: previous.operationId, manual: previous.manual, controller: new AbortController() };
        jobsRef.current.set(botId, resumed);
        trackStartup(bot, resumed);
      }
      updateBot(botId, { stopBusy: false, startBusy: false, launchUncertain: bot.startBusy || bot.launchUncertain });
      enqueueSnackbar('Не удалось подтвердить остановку. Проверьте статус или повторите остановку.', { variant: 'warning' });
    }
  };

  // Фильтрация ботов по типу
  const filteredBots = bots.filter(bot => {
    if (botTypeFilter === 'blocked') return bot.isBlocked;
    if (bot.isBlocked) return false;
    if (botTypeFilter === 'all') return true;
    return bot.botType === botTypeFilter;
  });

  if (botTypeFilter === 'blocked') {
    filteredBots.sort((a, b) => (Date.parse(b.blockedAt) || 0) - (Date.parse(a.blockedAt) || 0));
  }

  return (
    <Container>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Telegram боты
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {botTypeFilter === 'blocked' && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteBlocked}
              disabled={blockedBotCount === 0 || isRefreshing || isDeletingBlocked}
            >
              Удалить всех заблокированных ({blockedBotCount})
            </Button>
          )}
          <Button variant="outlined" onClick={() => { if (!deletingBlockedRef.current) fetchBots(); }} disabled={isRefreshing || isDeletingBlocked}>
            Обновить
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddDialogOpen(true)}
            disabled={isDeletingBlocked}
          >
            Добавить бота
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs value={botTypeFilter} onChange={(e, newValue) => setBotTypeFilter(newValue)}>
          <Tab label="Все боты" value="all" />
          <Tab label="Публичные" value="public" />
          <Tab label="Личные" value="personal" />
          <Tab label="Приватные" value="private" />
          <Tab label="Заблокированные" value="blocked" />
        </Tabs>
      </Box>

      {botTypeFilter === 'private' && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">Оператор приватных ботов</Typography>
          <Typography sx={{ mb: 2 }}>
            Единая настройка для всех приватных ботов: оператор указывается в сообщении об отказе в доступе. Перезапуск ботов не требуется.
          </Typography>
          {operatorStatus === 'loading' && <Typography role="status">Загрузка оператора...</Typography>}
          {operatorStatus === 'error' && (
            <Box role="alert" sx={{ mb: 2 }}>
              <Typography>Не удалось загрузить оператора. Настройка недоступна до успешной загрузки.</Typography>
              <Button onClick={() => setOperatorLoadAttempt(attempt => attempt + 1)}>Повторить загрузку</Button>
            </Box>
          )}
          <TextField
            label="Username оператора"
            fullWidth
            sx={{ my: 2 }}
            value={operatorUsername}
            disabled={operatorStatus !== 'ready'}
            error={Boolean(operatorValidation)}
            helperText={operatorValidation || 'Можно указать @username. Очистите поле, чтобы вернуть обращение к администратору.'}
            onChange={event => {
              setOperatorUsername(event.target.value);
              setOperatorValidation('');
            }}
          />
          {(operatorStatus === 'ready' || operatorStatus === 'saving') && validOperator && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Предпросмотр сообщения об отказе</Typography>
              <Typography>
                {normalizedOperator
                  ? `Доступ к этому боту ограничен. Обратитесь к оператору @${normalizedOperator}.`
                  : 'Доступ к этому боту ограничен. Обратитесь к администратору.'}
              </Typography>
            </Box>
          )}
          <Button variant="contained" onClick={handleSaveOperator} disabled={operatorStatus !== 'ready'}>
            Сохранить оператора
          </Button>
        </Card>
      )}

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Имя пользователя</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Токен</TableCell>
                <TableCell>Команды</TableCell>
                <TableCell>Пользователи</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBots.length === 0 && !isRefreshing && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {botTypeFilter === 'blocked' ? 'Заблокированных ботов нет' : 'Ботов нет'}
                  </TableCell>
                </TableRow>
              )}
              {filteredBots.map((bot) => (
                <TableRow key={bot._id}>
                  <TableCell>{bot.username}</TableCell>
                  <TableCell>
                    <Chip 
                      label={bot.botType === 'private' ? 'Приватный' : bot.botType === 'personal' ? 'Личный' : 'Публичный'} 
                      color={bot.botType === 'private' ? 'info' : bot.botType === 'personal' ? 'secondary' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bot.isBlocked ? 'Заблокирован' : bot.startBusy ? 'Запрос запуска...' :
                        isPending(bot.startup) ? (bot.startup.status === 'queued' ? 'В очереди' : 'Запускается') :
                        bot.launchUncertain ? 'Итог запуска неизвестен' : isRunning(bot) ? 'Запущен' :
                        bot.pollingBlocked ? 'Итог запуска неизвестен' : bot.startup?.status === 'failed' ? 'Ошибка запуска' : 'Остановлен'}
                      color={bot.isBlocked ? 'error' : isRunning(bot) ? 'success' : bot.startup?.status === 'failed' ? 'error' : 'default'}
                      size="small"
                      icon={bot.isBlocked || isPending(bot.startup) || bot.pollingBlocked ? undefined : isRunning(bot) ? <PlayArrowIcon /> : <StopIcon />}
                    />
                    {isPending(bot.startup) && bot.startup.stage && (
                      <Typography variant="body2">Этап: {formatStartError({ response: { data: { diagnostic: bot.startup } } }, bot).stage}</Typography>
                    )}
                    {bot.startup?.status === 'unknown' && !bot.pollingBlocked && <Typography role="status" variant="body2">{unknownStartup}</Typography>}
                    {bot.startup?.status === 'cancelled' && <Typography variant="body2">Запуск отменён</Typography>}
                    {bot.startup?.status === 'failed' && (
                      <Box>
                        <Typography variant="body2">Запуск завершился ошибкой</Typography>
                        <Button size="small" onClick={() => showStartError(bot, { response: { data: bot.startup } }, 'Сохранённая операция запуска')}>
                          Причина ошибки
                        </Button>
                      </Box>
                    )}
                    {bot.launchUncertain && <Typography variant="body2">Ответ не получен. Обновите статус перед повторным запуском.</Typography>}
                    {bot.pollError && (
                      <Box role="status">
                        <Typography variant="body2">{bot.pollError}</Typography>
                        {!bot.pollingBlocked && <Button size="small" onClick={() => jobsRef.current.get(bot._id)?.poll?.()}>Повторить проверку</Button>}
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Проверка Telegram (кэш): {bot.isAvailable === true ? 'был доступен' : bot.isAvailable === false ? 'был недоступен' : 'нет данных'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Время проверки: {bot.lastCheckedAt && !Number.isNaN(Date.parse(bot.lastCheckedAt))
                        ? `${new Date(bot.lastCheckedAt).toLocaleString('ru-RU')}${Date.now() - Date.parse(bot.lastCheckedAt) > 600000 ? ' — устарело' : ''}`
                        : 'неизвестно'}
                    </Typography>
                    {bot.statusError && (
                      <Typography variant="body2" color="text.secondary">
                        Ошибка проверки: {formatStartError({ response: { data: { message: 'Проверка недоступна', diagnostic: bot.statusError } } }, bot).message}
                      </Typography>
                    )}
                    {bot.isBlocked && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          Дата обнаружения: {bot.blockedAt && !Number.isNaN(Date.parse(bot.blockedAt))
                            ? new Date(bot.blockedAt).toLocaleString('ru-RU')
                            : 'неизвестна'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {bot.blockedReason === 'unauthorized'
                            ? 'Токен недействителен или отозван (401)'
                            : bot.blockedReason === 'deactivated'
                              ? 'Бот деактивирован Telegram'
                              : 'Причина блокировки неизвестна'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{bot.token.substring(0, 20)}...</TableCell>
                  <TableCell>
                    {bot.settings?.commands?.length || 0} команд
                  </TableCell>
                  <TableCell>
                    {bot.activationCount ?? 0}
                  </TableCell>
                  <TableCell>
                    {isRunning(bot) || isPending(bot.startup) || bot.launchUncertain ? (
                      <IconButton 
                        onClick={() => handleStopBot(bot._id)} 
                        color="error"
                        size="small"
                        title="Остановить бота"
                        disabled={isDeletingBlocked || bot.stopBusy}
                      >
                        <StopIcon />
                      </IconButton>
                    ) : (
                      <IconButton 
                        onClick={() => handleStartBot(bot._id)} 
                        color="success"
                        size="small"
                        title="Запустить бота"
                        disabled={bot.isBlocked || isDeletingBlocked || bot.startBusy || bot.stopBusy || bot.pollingBlocked}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={() => handleEdit(bot)} color="primary" size="small" aria-label="Редактировать бота" disabled={isDeletingBlocked}>
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      component={Link} 
                      to={`/telegram-bots/${bot._id}/users`} 
                      aria-label="Пользователи бота"
                      title="Пользователи бота"
                      color="info"
                      size="small"
                    >
                      <People />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(bot._id)} color="error" size="small" aria-label="Удалить бота" disabled={isDeletingBlocked}>
                      <DeleteIcon />
                    </IconButton>
                    {bot.botType === 'private' && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        disabled={isDeletingBlocked}
                        onClick={() => { if (!deletingBlockedRef.current) setAccessBot(bot); }}
                      >
                        Доступ
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={() => handleOpenBroadcast(bot._id)}
                      disabled={bot.isBlocked || isDeletingBlocked}
                    >
                      Рассылка
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={Boolean(startError)}
        onClose={() => setStartError(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="bot-start-error-title"
        aria-describedby="bot-start-error-message"
      >
        <DialogTitle id="bot-start-error-title">Ошибка запуска бота</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Бот: {startError?.botName}</Typography>
          <Typography id="bot-start-error-message" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {startError?.message}
          </Typography>
          <Box sx={{ mt: 2, overflowWrap: 'anywhere' }}>
            <Typography variant="body2">HTTP: {startError?.httpStatus}</Typography>
            <Typography variant="body2">Формат ответа: {startError?.responseFormat}</Typography>
            {startError?.requestCode && <Typography variant="body2">Код запроса: {startError.requestCode}</Typography>}
            {startError?.stage && <Typography variant="body2">Этап: {startError.stage}</Typography>}
            {startError?.code && <Typography variant="body2">Код backend: {startError.code}</Typography>}
            <Typography variant="body2">{startError?.request}</Typography>
            <Typography variant="body2">Время: {startError?.time}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartError(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <AddBotDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={() => {
          fetchBots();
          setIsAddDialogOpen(false);
        }}
      />

      <EditBotDialog
        open={isEditDialogOpen}
        bot={selectedBot}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedBot(null);
        }}
        onSuccess={() => {
          fetchBots();
          setIsEditDialogOpen(false);
          setSelectedBot(null);
        }}
      />
      {accessBot && (
        <PrivateBotAccessDialog
          key={accessBot._id}
          open
          bot={accessBot}
          onClose={() => setAccessBot(null)}
        />
      )}
      {/* Модальное окно рассылки */}
      <Dialog open={isBroadcastOpen} onClose={handleCloseBroadcast} maxWidth="sm" fullWidth>
        <DialogTitle>Рассылка сообщения</DialogTitle>
        <DialogContent>
          <TextField
            label="Текст сообщения"
            multiline
            minRows={4}
            fullWidth
            value={broadcastText}
            onChange={e => setBroadcastText(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBroadcast}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleSendBroadcast}
            disabled={bots.find(bot => bot._id === broadcastBotId)?.isBlocked}
          >
            Отправить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

